/** Credit economy rules — Option D monthly reset (see cursor-handoff). */

export const CREDIT_CAPS = {
  fan: 5000,
  creator: 8000,
};

export const MONTHLY_CHECKIN_EARN_CAP = 2000;
export const LOYALTY_CARRY_PERCENT = 0.1;
export const LOYALTY_CARRY_MIN = 50;
export const NEW_USER_GRACE_DAYS = 60;

export const STARTER_PACK = {
  credits: 500,
  priceCad: 4.99,
  id: "starter_pack",
};

export const TIP_PRESETS = [
  { id: "coffee", label: "Coffee", emoji: "☕", amount: 2, desc: "A warm start" },
  { id: "slice", label: "Slice", emoji: "🍕", amount: 5, desc: "A little treat" },
  { id: "dessert", label: "Fancy Dessert", emoji: "🧁", amount: 10, desc: "You deserve it" },
  { id: "beaver", label: "Beaver", emoji: "🦫", amount: 20, desc: "The most Canadian tip 🍁" },
];

export const TIP_LIMITS = {
  minCustom: 1,
  maxCustom: 10000,
  maxPerCreatorPerMonth: 3,
  platformFeeRate: 0.1,
};

export const EARN = {
  dailyCheckIn: 10,
  streak7: 50,
  streak30: 200,
  first100Viewer: 50,
  referFan: 150,
  referCreator: 300,
  referFanMonthlyCap: 5,
  referCreatorMonthlyCap: 2,
  firstDropEver: 100,
  drop100Views: 100,
  milestone10Drops: 200,
  milestone100Subs: 500,
  milestone1000Subs: 2000,
  wallNoteReceived: 50,
  nominationReceived: 25,
  tipBonusPercent: 0.1,
};

export const SPEND = {
  nomination: { fan: 50, creator: 100 },
  superComment: { fan: 100, creator: 200 },
  platformTheme: 300,
  fanBadge: 1000,
  wallNote: { fan: 750, creator: 1000 },
  dropDedication: 200,
  firstViewerPlaque: 500,
  shoutoutRequest: 1000,
  anniversaryBadge: 300,
  boostOwnDrop: 500,
  freeUnlock: {
    tier1Max: 5,
    tier1Credits: 700,
    tier2Max: 10,
    tier2Credits: 1400,
    monthlyCap: 2,
  },
};

export const FAN_SPEND_SECTIONS = [
  {
    id: "support",
    title: "Support Creators",
    items: [
      { id: "nomination", name: "Nominate for Featured", cost: SPEND.nomination.fan, desc: "50 credits · max 1 per creator per day" },
      { id: "super_comment", name: "Super Comment", cost: SPEND.superComment.fan, desc: "Pin your comment for 48 hours on an unlocked drop" },
      { id: "shoutout_request", name: "Creator Shoutout Request", cost: SPEND.shoutoutRequest, desc: "Request a shoutout on their next drop" },
    ],
  },
  {
    id: "appreciation",
    title: "Appreciation",
    items: [
      { id: "wall_note", name: "Appreciation Wall Note", cost: SPEND.wallNote.fan, desc: "250–500 characters · max 1 per creator per month" },
      { id: "drop_dedication", name: "Drop Dedication", cost: SPEND.dropDedication, desc: "Dedicate a drop to someone special" },
      { id: "anniversary_badge", name: "Anniversary Badge", cost: SPEND.anniversaryBadge, desc: "Show how long you've followed a creator (6+ months)" },
    ],
  },
  {
    id: "collect",
    title: "Collect",
    items: [
      { id: "first_viewer_plaque", name: "First Viewer Plaque", cost: SPEND.firstViewerPlaque, desc: "Gold glow on the first 100 wall forever" },
      { id: "free_unlock", name: "Unlock Paid Drop Free", cost: "700–1,400", desc: "Max 2/month · drops $10 and under only" },
    ],
  },
  {
    id: "personalize",
    title: "Personalize",
    items: [
      { id: "platform_theme", name: "App Look", cost: SPEND.platformTheme, desc: "One-time unlock per app look" },
      { id: "fan_badge", name: "Exclusive Fan Badge", cost: SPEND.fanBadge, desc: "Permanent badge on profile and in comments" },
    ],
  },
];

export const CREATOR_SPEND_SECTIONS = [
  {
    id: "creator_spend",
    title: "Spend as a Creator",
    items: [
      { id: "nomination", name: "Nominate a Creator", cost: SPEND.nomination.creator, desc: "Support a fellow creator for Community's Choice" },
      { id: "wall_note", name: "Wall Note to Creator", cost: SPEND.wallNote.creator, desc: "Send appreciation to another creator" },
      { id: "boost_drop", name: "Boost Your Drop", cost: SPEND.boostOwnDrop, desc: "24h Gallery visibility in your niche" },
      { id: "super_comment", name: "Super Comment", cost: SPEND.superComment.creator, desc: "Pin your comment on another creator's drop" },
      { id: "platform_theme", name: "App Look", cost: SPEND.platformTheme, desc: "One-time unlock per app look" },
    ],
  },
  {
    id: "themes",
    title: "Themes",
    items: [
      {
        id: "profile_themes",
        name: "Theme Catalog",
        cost: "1,800+",
        desc: "Unlock looks for your creator profile with credits or milestones",
        href: "/themes",
      },
    ],
  },
];

export function creditCapForRole(isCreator) {
  return isCreator ? CREDIT_CAPS.creator : CREDIT_CAPS.fan;
}

export function loyaltyBonusFromBalance(balance) {
  const fromPercent = Math.floor((balance || 0) * LOYALTY_CARRY_PERCENT);
  return Math.max(fromPercent, LOYALTY_CARRY_MIN);
}

export function freeUnlockCreditsForPrice(priceCad) {
  const p = Number(priceCad) || 0;
  if (p <= 0 || p > SPEND.freeUnlock.tier2Max) return null;
  if (p <= SPEND.freeUnlock.tier1Max) return SPEND.freeUnlock.tier1Credits;
  return SPEND.freeUnlock.tier2Credits;
}

export function tipNotificationMessage(amountCad, fanName, anonymous) {
  const name = anonymous ? "Someone" : fanName || "Someone";
  const a = Number(amountCad);
  if (a === 2) return `☕ ${name} just bought you a coffee`;
  if (a === 5) return `🍕 ${name} just bought you a slice`;
  if (a === 10) return `🧁 ${name} just treated you to a fancy dessert`;
  if (a === 20) return `🦫 ${name} sent you the most Canadian tip`;
  return `✦ ${name} sent you a $${a} tip`;
}

export function daysUntilMonthEnd(date = new Date()) {
  const end = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  end.setHours(23, 59, 59, 999);
  return Math.ceil((end - date) / (1000 * 60 * 60 * 24));
}
